import { useCrmStore } from "../../stores/crm";
import trash from "../../assets/icons/darkIcons/trash.svg";
import { deleteBillingContact } from "../../api/customerFile";
import { showToastError, showToastSuccess } from "../../services/toast";
import { IInterlocuteur } from "../../interfaces/crm";
import { ModalProps } from "../../interfaces/modal";
import send from "../../assets/icons/darkIcons/send.svg";
import { sendAccess } from "../../api/users";
import { ModalButton, ModalShell } from "../ui/modals/ModalShell";

const InterlocuteursSection: React.FC<ModalProps> = ({
  idCustomer,
  closeModal,
}: any) => {
  const { billingContacts, setBillingContacts, updateCrm, setUpdateCrm } =
    useCrmStore();

  const handleAddInterlocuteur = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    setBillingContacts([
      ...billingContacts,
      {
        email: "",
        clientAccess: false,
        id: undefined,
        lastConnection: null,
      } as IInterlocuteur, // Ajout d'un nouvel objet vide
    ]);
  };

  // Fonction pour modifier un champ email
  const handleEmailChange = (index: number, value: string) => {
    const updatedInterlocuteurs = [...billingContacts];
    updatedInterlocuteurs[index].email = value;
    setBillingContacts(updatedInterlocuteurs);
  };

  // Fonction pour modifier un champ checkbox
  const handleCheckboxChange = (index: number) => {
    const updatedInterlocuteurs = [...billingContacts];
    updatedInterlocuteurs[index].clientAccess =
      !updatedInterlocuteurs[index].clientAccess;
    setBillingContacts(updatedInterlocuteurs);
  };

  const deleteInterlocuteur = (id: number | undefined) => {
    if (!id) {
      // Si l'interlocuteur n'a pas d'ID, on l'enlève juste du state local
      setBillingContacts(
        billingContacts.filter(
          (interlocuteur) =>
            interlocuteur.id !== id && interlocuteur.id !== undefined
        )
      );
      return;
    }

    deleteBillingContact(idCustomer, id)
      .then(() => {
        showToastSuccess("Interlocuteur supprimé");
        setUpdateCrm(!updateCrm);
        setBillingContacts([]);
      })
      .catch(() => {
        showToastError("Une erreur est survenue");
      });
  };

  const handleSendAccess = (idUser: number) => {
    sendAccess(idUser)
      .then(() => {
        showToastSuccess("Accès renvoyé");
      })
      .catch(() => {
        showToastError("Une erreur est survenue");
      });
  };

  return (
    <ModalShell
      title="Interlocuteurs de facturation"
      size="lg"
      onClose={() => closeModal()}
      footer={
        <ModalButton
          variant="primary"
          onClick={(e) => {
            e.preventDefault();
            closeModal();
          }}
        >
          Valider
        </ModalButton>
      }
    >
      <div className="mb-3 flex justify-end">
        <ModalButton variant="secondary" onClick={(e) => handleAddInterlocuteur(e)}>
          Ajouter un interlocuteur
        </ModalButton>
      </div>

      {/* Tableau des interlocuteurs */}
      <div className="overflow-hidden rounded-xl border border-slate-200">
        <div className="hidden border-b border-slate-100 bg-slate-50 px-4 py-2.5 sm:flex">
          <div className="w-1/2 text-[11px] font-bold tracking-wider text-slate-500 uppercase">
            Email
          </div>
          <div className="w-1/6 text-[11px] font-bold tracking-wider text-slate-500 uppercase">
            Espace client
          </div>
          <div className="w-1/6 text-[11px] font-bold tracking-wider text-slate-500 uppercase">
            Accès
          </div>
          <div className="w-1/6 text-[11px] font-bold tracking-wider text-slate-500 uppercase">
            Supprimer
          </div>
        </div>

        <div className="max-h-80 divide-y divide-slate-100 overflow-y-auto">
          {billingContacts.length === 0 ? (
            <p className="px-4 py-8 text-center font-Nunito text-sm text-slate-400">
              Aucun interlocuteur
            </p>
          ) : (
            billingContacts.map((interlocuteur, i) => (
              <div
                key={i}
                className="flex flex-col gap-2 px-4 py-2.5 transition-colors hover:bg-slate-50 sm:flex-row sm:items-center sm:gap-0"
              >
                <div className="min-w-0 sm:w-1/2 sm:pr-3">
                  <input
                    type="text"
                    value={interlocuteur.email}
                    onChange={(e) => handleEmailChange(i, e.target.value)}
                    placeholder="Saisissez l'email"
                    className="h-9 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 font-Nunito text-sm text-slate-800 transition-colors placeholder:text-slate-400 focus:border-indigo-300 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:outline-none"
                  />
                </div>
                <div className="flex items-center gap-2 sm:w-1/6">
                  <span className="text-[11px] font-bold tracking-wider text-slate-400 uppercase sm:hidden">
                    Espace client
                  </span>
                  <input
                    type="checkbox"
                    name={`clientSpace-${i}`}
                    id={`clientSpace-${i}`}
                    checked={interlocuteur.clientAccess}
                    onChange={() => handleCheckboxChange(i)}
                    className="size-4 cursor-pointer accent-dca-blue"
                  />
                </div>
                <div className="flex items-center gap-1 sm:w-1/3">
                  <button
                    className="inline-flex size-9 cursor-pointer items-center justify-center rounded-xl text-slate-400 transition-colors hover:bg-indigo-50 hover:text-dca-blue"
                    title="Envoyer l'accès"
                    onClick={(e) => {
                      e.preventDefault();
                      handleSendAccess(Number(interlocuteur.idUser));
                    }}
                  >
                    <img src={send} alt="envoyer l'accès" className="size-4" />
                  </button>
                  <button
                    className="inline-flex size-9 cursor-pointer items-center justify-center rounded-xl text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                    title="Supprimer"
                    onClick={(e) => {
                      e.preventDefault();
                      deleteInterlocuteur(interlocuteur.id);
                    }}
                  >
                    <img src={trash} alt="poubelle" className="size-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </ModalShell>
  );
};

export default InterlocuteursSection;
